import { AloftPrice, isDefined } from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import { getPricePerSquareFoot } from '../../../../modules/costApproach/utils/getPricePerSquareFoot.ts';
import { selectZipOnlyGarageCarportCost } from '../costSelectors/selectGarageCarportCost.ts';
import {
  selectExternalDepreciationDisplay,
  selectFunctionalDepreciationDisplay,
  selectPhysicalDepreciationDisplay,
} from '../depreciationSelectors.ts';
import { selectOpinionOfSiteValueDisplay } from '../opinionOfSiteValueSelectors.ts';
import { selectCostMultiplier } from '../orderSelectors.ts';
import { selectImprovementsGla } from '../propertyDataSelectors/improvementsSelectors.ts';
import { selectCostNewDisplay, selectZipOnlyAdditionalStructureCostNew } from '../selectCostNew.ts';
import { selectBaseGlaAndBathCostNew } from '../selectGlaAndBathCostNew.ts';
import { selectSubjectValueDisplay } from '../selectSubjectValue.ts';
import { selectZipOnlyBasementCost } from '../selectTotalBasementCost.ts';

type Data<T extends string | null | (string | null)[]> = {
  label: string;
  value: T;
};

function makeData<T extends string | null | (string | null)[]>(label: string, value: T): Data<T> {
  return {
    label,
    value,
  };
}

export const selectManufacturedCostApproachTableData = createSelector(
  [
    selectOpinionOfSiteValueDisplay,
    selectImprovementsGla,
    selectBaseGlaAndBathCostNew,
    selectZipOnlyBasementCost,
    selectZipOnlyAdditionalStructureCostNew,
    selectZipOnlyGarageCarportCost,
    selectCostNewDisplay,
    selectCostMultiplier,
    selectPhysicalDepreciationDisplay,
    selectFunctionalDepreciationDisplay,
    selectExternalDepreciationDisplay,
    selectSubjectValueDisplay,
  ],
  (
    opinionOfSiteValueDisplay,
    improvementsGLA,
    baseGLAAndBathCostNew,
    zipOnlyBasementCost,
    zipOnlyAdditionalStructureCostNew,
    zipOnlyGarageCarportCost,
    costNew,
    costMultiplier,
    physicalDepreciation,
    functionalDepreciation,
    externalDepreciation,
    subjectValue,
  ) => {
    const pricePerSqFt = getPricePerSquareFoot(improvementsGLA, baseGLAAndBathCostNew);

    // TODO: Section one, structures, and sub-total sans custom multiplier
    const structures: Data<string>[] = [];
    if (zipOnlyBasementCost.gt(AloftPrice.zero)) {
      structures.push(makeData('Basement', zipOnlyBasementCost.toString(true)));
    }
    if (zipOnlyAdditionalStructureCostNew?.gt(AloftPrice.zero)) {
      structures.push(
        makeData('Additional Structures', zipOnlyAdditionalStructureCostNew.toString(true)),
      );
    }
    if (zipOnlyGarageCarportCost?.gt(AloftPrice.zero)) {
      structures.push(makeData('Garage/Carport', zipOnlyGarageCarportCost.toString(true)));
    }

    const subTotal = AloftPrice.add(
      baseGLAAndBathCostNew,
      zipOnlyBasementCost,
      zipOnlyAdditionalStructureCostNew,
      zipOnlyGarageCarportCost,
    );

    return {
      columnHeaders: {
        left: 'Feature',
        right: 'Cost Data',
      },
      opinionOfSiteValue: makeData('Opinion of Site Value', opinionOfSiteValueDisplay),
      sectionOne: makeData('Section One', [
        improvementsGLA?.toSqFt().toString() ?? null,
        pricePerSqFt ? `${pricePerSqFt.toString()} per sqft` : null,
        baseGLAAndBathCostNew?.toString(true) ?? '',
      ]),
      sectionTwo: makeData('Section Two', [null, null, null]),
      sectionThree: makeData('Section Three', [null, null, null]),
      sectionFour: makeData('Section Four', [null, null, null]),
      structures,
      subTotal: makeData('Sub-total', subTotal.toString(true)),
      costMultiplier: makeData(
        'Cost Multiplier (if applicable)',
        isDefined(costMultiplier) ? String(costMultiplier) : '',
      ),
      modifiedSubTotal: makeData('Modified Sub-total', costNew ?? ''),
      physicalDepreciation: makeData(
        'Physical Depreciation or Condition Modifier',
        physicalDepreciation ? `(${physicalDepreciation})` : '',
      ),
      functionalObsolescence: makeData(
        'Functional Obsolescence',
        functionalDepreciation ? `(${functionalDepreciation})` : '',
      ),
      externalDepreciation: makeData(
        'External Depreciation or State Location Modifier',
        externalDepreciation ? `(${externalDepreciation})` : '',
      ),
      deliveryInstallationSetup: makeData('Delivery, Installation and Setup', 'Included in costs'),
      otherDepreciatedImprovements: makeData(
        'Other Depreciated Site Improvements',
        'Included in site value',
      ),
      marketValue: makeData('Market Value of Subject Site', opinionOfSiteValueDisplay),
      indicatedValue: makeData('Indicated Value by Cost Approach', subjectValue ?? ''),
    };
  },
);
